/**
 * Bundle 151 - Admin reconciliation queue for one event.
 *
 * Route: /admin/events/:eid/reconciliation
 *
 * Lists every registration in payment_status IN
 * (user_marked_paid, instructions_viewed,
 *  auto_verified_pending_review). Two actions per row: Verify
 * (flips to confirmed+verified, fires ticket email) or Reject
 * (flips to pending_payment + sends payment_rejected_v1 email
 * with a fresh resume link). Reject requires a reason.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { RefreshCcw, Check, X, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const fmtMoney = (cents, cur = 'USD') => {
  if (cents == null) return '-';
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} ${(cur || 'USD').toUpperCase()}`;
};

const EventReconciliationPage = () => {
  const { eid } = useParams();
  const [data, setData] = useState({ rows: [], count: 0, event: {} });
  const [loading, setLoading] = useState(true);
  const [busyRow, setBusyRow] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(null); // row
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(
        `/admin/events/${eid}/reconciliation`);
      setData(d || { rows: [], count: 0, event: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Could not load the reconciliation queue.');
    } finally { setLoading(false); }
  }, [eid]);

  useEffect(() => { load(); }, [load]);

  const doVerify = async (row) => {
    setBusyRow(row.registration_id);
    try {
      await api.post(
        `/admin/events/${eid}/registrations/${row.registration_id}/reconcile/verify`,
        {});
      toast.success('Verified. Ticket email dispatched.');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Verify failed.');
    } finally { setBusyRow(null); }
  };

  const doReject = async () => {
    if (!rejectOpen) return;
    const reason = (rejectReason || '').trim();
    if (reason.length < 3) {
      toast.error('A short reason is required.');
      return;
    }
    setBusyRow(rejectOpen.registration_id);
    try {
      await api.post(
        `/admin/events/${eid}/registrations/${rejectOpen.registration_id}/reconcile/reject`,
        { reason });
      toast.success('Rejected. Retry link emailed to the guest.');
      setRejectOpen(null); setRejectReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail
        || 'Reject failed.');
    } finally { setBusyRow(null); }
  };

  return (
    <div className="p-8 max-w-7xl space-y-6"
             data-testid="admin-event-reconciliation-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1">
            <Link to="/admin/events" className="inline-flex items-center gap-1 hover:text-primary-700">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to events
            </Link>
          </div>
          <h1 className="font-display text-3xl font-bold text-primary-900">
            Payment reconciliation
          </h1>
          <p className="text-neutral-600 text-sm mt-1 max-w-3xl">
            Every RSVP that claimed a manual payment (Zelle / Venmo / PayPal)
            or auto-matched but still awaits review lives here. Verify to
            confirm the spot and fire the ticket email. Reject with a note if
            the payment doesn&apos;t match your bank statement; the guest gets
            a fresh retry link.
          </p>
          {data.event?.title && (
            <div className="mt-3 text-sm text-primary-900 font-semibold" data-testid="recon-event-title">
              {data.event.title}
            </div>
          )}
        </div>
        <Button variant="outline" onClick={load}
                    data-testid="recon-refresh-btn">
          <RefreshCcw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Card className="border shadow-soft">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-secondary-700">
              Pending reconciliation
            </div>
            <Badge className="bg-amber-500 text-amber-950" data-testid="recon-count-badge">
              {data.count || 0}
            </Badge>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="recon-table">
              <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase">
                <tr>
                  <th className="text-left p-2">Guest</th>
                  <th className="text-left p-2">Amount</th>
                  <th className="text-left p-2">Method</th>
                  <th className="text-left p-2">Memo</th>
                  <th className="text-left p-2">User ref</th>
                  <th className="text-left p-2">Marked at</th>
                  <th className="text-right p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center text-neutral-500" data-testid="recon-empty-state">
                    {loading ? 'Loading...' : 'No pending rows. All clear.'}
                  </td></tr>
                ) : data.rows.map((row) => (
                  <tr key={row.registration_id} className="border-t border-neutral-100"
                            data-testid={`recon-row-${row.registration_id}`}>
                    <td className="p-2">
                      <div className="text-primary-900 font-semibold">
                        {row.full_name || row.first_name || row.email_masked}
                      </div>
                      <div className="text-[11px] text-neutral-500">{row.email_masked}</div>
                    </td>
                    <td className="p-2 text-primary-900">
                      {fmtMoney(row.amount_cents, row.currency)}
                    </td>
                    <td className="p-2 capitalize">
                      {row.payment_method || '-'}
                    </td>
                    <td className="p-2 font-mono text-[11px] text-secondary-700">
                      {row.manual_reference || '-'}
                    </td>
                    <td className="p-2 text-[11px] text-neutral-700">
                      {row.manual_user_reference || '-'}
                    </td>
                    <td className="p-2 text-[11px] text-neutral-500">
                      {row.user_marked_paid_at
                        ? new Date(row.user_marked_paid_at).toLocaleString()
                        : (row.updated_at
                            ? new Date(row.updated_at).toLocaleString()
                            : '-')}
                    </td>
                    <td className="p-2">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm"
                                    onClick={() => doVerify(row)}
                                    disabled={busyRow === row.registration_id}
                                    className="bg-success-600 hover:bg-success-700 text-white"
                                    data-testid={`recon-verify-btn-${row.registration_id}`}>
                          <Check className="h-3.5 w-3.5 mr-1" /> Verify
                        </Button>
                        <Button size="sm" variant="outline"
                                    onClick={() => { setRejectOpen(row); setRejectReason(''); }}
                                    disabled={busyRow === row.registration_id}
                                    className="border-red-300 text-red-700 hover:bg-red-50"
                                    data-testid={`recon-reject-btn-${row.registration_id}`}>
                          <X className="h-3.5 w-3.5 mr-1" /> Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!rejectOpen} onOpenChange={(o) => { if (!o) { setRejectOpen(null); setRejectReason(''); } }}>
        <DialogContent className="max-w-md" data-testid="recon-reject-modal">
          <DialogHeader>
            <DialogTitle>Reject payment claim</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <p className="text-sm text-neutral-600">
              This flips the RSVP back to pending payment and emails the guest a fresh retry link.
            </p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Why couldn't you match this payment? The guest will see this."
              rows={4}
              data-testid="recon-reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRejectOpen(null); setRejectReason(''); }}
                        data-testid="recon-reject-cancel-btn">
              Cancel
            </Button>
            <Button onClick={doReject}
                        disabled={!!busyRow || (rejectReason || '').trim().length < 3}
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="recon-reject-confirm-btn">
              Reject payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventReconciliationPage;
